import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { isMobile, isNativeEmbedEditorPlugin } from '@databases/modes';
import { DatabaseLayoutType } from '@databases/types/database/view';
import React, { type FunctionComponent, useMemo } from 'react';
import { Panel } from 'react-resizable-panels';
import styled, { css } from 'styled-components';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import PanelSizes from './panel/sizes.ts';

const ToolbarContainer = styled.div<{
  isMobile: boolean;
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isRightGutterCollapsed: boolean;
  isSidePanelVisible: boolean;
  hasSidePanelGutter: boolean;
  isReadOnly: boolean;
  isInAIFCStagingAreaView: boolean;
  isObjectSidebarShown: boolean;
}>`
  ${(prop) => {
    if (prop.isMobile) {
      return css`
        padding-left: ${token('space.100', '8px')};
        padding-right: ${token('space.100', '8px')};
      `;
    }

    if (prop.isEmbedView) {
      return css`
        padding-left: 0;
      `;
    }

    if (prop.isNativeEmbedView) {
      return css`
        padding-left: 0;
      `;
    }

    if (prop.isReadOnly) {
      return css`
        padding-left: ${token('space.300', '24px')};
      `;
    }
    if (prop.hasSidePanelGutter) {
      return css`
        padding: 0 ${token('space.300', '24px')} 0 ${token('space.600', '48px')};
      `;
    }
    if (prop.isRightGutterCollapsed || prop.isSidePanelVisible) {
      return css`
        padding: 0 0 0 ${token('space.600', '48px')};
      `;
    }

    if (prop.isInAIFCStagingAreaView) {
      return css`
        padding-right: ${token('space.600', '48px')};
        padding-left: ${token('space.600', '48px')};
      `;
    }
    // When the ADS panel system FG is ON and the external object sidebar is open, it is
    // rendered outside the database iframe and narrows it, so the toolbar must not reserve
    // right padding. While the sidebar is closed the iframe is full width, so we keep the
    // original right padding.
    if (
      prop.isObjectSidebarShown &&
      isFeatureFlagEnabled('adsPanelSystemConfluenceObjectSidebar')
    ) {
      return css`
        padding-left: ${token('space.600', '48px')};
      `;
    }
    return css`
      padding-right: ${token('space.800', '64px')};
      padding-left: ${token('space.600', '48px')};
    `;
  }}
`;

export const ToolbarRenderer = ({
  isEmbedView,
  isNativeEmbedView,
  isRightGutterCollapsed,
  isSidePanelVisible,
  hasSidePanelGutter,
  isObjectSidebarShown = false,
  toolbar,
  isReadOnly
}: {
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isRightGutterCollapsed: boolean;
  isSidePanelVisible: boolean;
  hasSidePanelGutter: boolean;
  isObjectSidebarShown?: boolean;
  toolbar: React.ReactNode;
  isReadOnly?: boolean;
}) => {
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  return (
    <ToolbarContainer
      isMobile={isMobile()}
      isEmbedView={isEmbedView}
      isNativeEmbedView={isNativeEmbedView}
      isRightGutterCollapsed={isRightGutterCollapsed}
      isSidePanelVisible={isSidePanelVisible}
      hasSidePanelGutter={hasSidePanelGutter}
      isReadOnly={isReadOnly}
      isInAIFCStagingAreaView={isInAIFCStagingAreaView}
      isObjectSidebarShown={isObjectSidebarShown}
      id="layout-toolbar-wrapper"
      data-testid="layout-toolbar-wrapper"
    >
      {toolbar}
    </ToolbarContainer>
  );
};

const renderViewWrapper = (BaseComponent: 'div' | typeof Panel) =>
  styled(BaseComponent)<{
    $isMobile: boolean;
    $isEmbed: boolean;
    $isNativeEmbedView: boolean;
    $isNativeEmbedEditorPlugin: boolean;
    $hasSidePanelGutter: boolean;
    $isRightGutterCollapsed: boolean;
    $isInAIFCStagingAreaView: boolean;
    $isObjectSidebarShown: boolean;
    $isTimelineLayoutEnabled: boolean;
    $layoutType?: DatabaseLayoutType;
  }>`
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    height: 100%;

    ${(props) => {
      if (props.$isMobile) {
        return css`
          padding-left: ${token('space.0', '0px')};
          padding-right: ${token('space.0', '0px')};
          max-width: 100vw;
        `;
      }

      if (props.$isEmbed) {
        return css`
          padding-left: ${token('space.100', '8px')};
          padding-right: ${token('space.100', '8px')};
        `;
      }

      if (props.$isNativeEmbedView) {
        const isTable = props.$layoutType === DatabaseLayoutType.TABLE;
        const isFullWidthLayout =
          isTable ||
          (props.$isTimelineLayoutEnabled &&
            props.$layoutType === DatabaseLayoutType.TIMELINE);

        if (props.$isNativeEmbedEditorPlugin) {
          return css`
            /* Negative margin here offsets the top spacing owned by full-width layouts */
            margin-top: ${isFullWidthLayout ? token('space.negative.100') : 0};
            padding-bottom: ${token('space.0', '0px')};
            padding-left: ${token('space.0', '0px')};
            padding-right: ${token('space.0', '0px')};
          `;
        }

        return css`
          padding-bottom: ${token('space.400', '32px')};
          padding-left: ${
            isFullWidthLayout ? token('space.0', '0px') : token('space.300', '24px')
          };
          padding-right: ${token('space.300', '24px')};
        `;
      }

      if (props.$hasSidePanelGutter) {
        return css`
          padding-left: ${token('space.300', '24px')};
          padding-right: ${token('space.300', '24px')};
        `;
      }

      if (props.$isRightGutterCollapsed) {
        return css`
          padding: 0 0 0 ${token('space.300', '24px')};
        `;
      }

      if (props.$isInAIFCStagingAreaView) {
        return css`
          padding-left: ${token('space.200', '16px')};
          padding-right: ${token('space.600', '48px')};
        `;
      }

      // When the ADS panel system FG is ON and the external object sidebar is open, it is
      // rendered outside the database iframe and narrows it, so the view wrapper must not
      // constrain its max-width. While the sidebar is closed the iframe is full width, so we
      // keep the original max-width restriction.
      if (
        props.$isObjectSidebarShown &&
        isFeatureFlagEnabled('adsPanelSystemConfluenceObjectSidebar')
      ) {
        return css`
          padding-left: ${token('space.300', '24px')};
        `;
      }

      return css`
        max-width: calc(
          100vw - ${token('space.800', '64px')} - ${token('space.300', '24px')}
        );
        padding-left: ${token('space.300', '24px')};
      `;
    }}
  `;

export interface MainPanelProps {
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isRightGutterCollapsed: boolean;
  isSidePanelVisible: boolean;
  isEntryViewPanelVisible: boolean;
  /**
   * Whether the external AFM object sidebar (rendered outside the iframe when the ADS panel
   * system FG is on) is currently shown. Only meaningful while that FG is enabled.
   */
  isObjectSidebarShown: boolean;
  options: {
    toolbarLocation: 'top' | 'views';
  };
  view: React.ReactNode;
  toolbar: React.ReactNode;
  footer?: React.ReactNode;
  isReadOnly?: boolean;
  layoutType?: DatabaseLayoutType;
}

type SidePanelGutterOptions = Pick<
  MainPanelProps,
  | 'isRightGutterCollapsed'
  | 'isSidePanelVisible'
  | 'isEntryViewPanelVisible'
  | 'isObjectSidebarShown'
  | 'layoutType'
> & {
  isInAIFCStagingAreaView: boolean;
  isTimelineLayoutEnabled: boolean;
};

const hasTimelineSidePanelGutter = ({
  isSidePanelVisible,
  isTimelineLayoutEnabled,
  layoutType
}: SidePanelGutterOptions) =>
  isSidePanelVisible &&
  isTimelineLayoutEnabled &&
  layoutType === DatabaseLayoutType.TIMELINE;

const hasSpecializedDesktopSpacing = ({
  isRightGutterCollapsed,
  isObjectSidebarShown,
  isInAIFCStagingAreaView
}: SidePanelGutterOptions) =>
  isRightGutterCollapsed ||
  isInAIFCStagingAreaView ||
  (isObjectSidebarShown && isFeatureFlagEnabled('adsPanelSystemConfluenceObjectSidebar'));

const shouldApplySidePanelGutter = (options: SidePanelGutterOptions) =>
  hasTimelineSidePanelGutter(options) ||
  (options.isEntryViewPanelVisible && !hasSpecializedDesktopSpacing(options));

const mainPanelStackStyles = xcss({
  height: '100%',
  position: 'relative'
});

const footerContainerStyles = xcss({
  position: 'absolute',
  left: 'space.300',
  right: 'space.300',
  bottom: 'space.300',
  maxWidth: '795px',
  marginInline: 'auto',
  zIndex: 'blanket',
  pointerEvents: 'none'
});

const footerContentStyles = xcss({
  pointerEvents: 'auto'
});

const ViewWrapper = renderViewWrapper('div');

/**
 * Renders the main (left) panel of DatabaseLayout
 */
const MainPanel: FunctionComponent<MainPanelProps> = ({
  isEmbedView,
  isNativeEmbedView,
  isRightGutterCollapsed,
  isSidePanelVisible,
  isEntryViewPanelVisible,
  isObjectSidebarShown,
  options,
  view,
  toolbar,
  footer,
  isReadOnly,
  layoutType
}) => {
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const isMobileApp = isMobile();
  const isTimelineLayoutEnabled = useMemo(
    () =>
      getExperimentValue('databasesRoadmap', 'isEnabled', {
        fireExperimentExposure: false
      }) === true,
    []
  );
  const hasSidePanelGutter = shouldApplySidePanelGutter({
    isRightGutterCollapsed,
    isSidePanelVisible,
    isEntryViewPanelVisible,
    isObjectSidebarShown,
    isInAIFCStagingAreaView,
    isTimelineLayoutEnabled,
    layoutType
  });
  const shouldOffsetTableForPillTabs =
    isFeatureFlagEnabled('databasesPillViewTabs') &&
    layoutType === DatabaseLayoutType.TABLE;

  return (
    <Panel
      order={PanelSizes.main.order}
      maxSize={PanelSizes.main.max}
      minSize={PanelSizes.main.min}
      id="main"
    >
      <Stack xcss={mainPanelStackStyles}>
        {options.toolbarLocation === 'top' ? (
          <ToolbarRenderer
            isEmbedView={isEmbedView}
            isNativeEmbedView={isNativeEmbedView}
            isRightGutterCollapsed={isRightGutterCollapsed}
            isSidePanelVisible={isSidePanelVisible}
            hasSidePanelGutter={hasSidePanelGutter}
            isObjectSidebarShown={isObjectSidebarShown}
            toolbar={toolbar}
            isReadOnly={isReadOnly}
          />
        ) : null}
        <ViewWrapper
          $isMobile={isMobileApp}
          $isEmbed={isEmbedView}
          $isNativeEmbedView={isNativeEmbedView}
          $isNativeEmbedEditorPlugin={isNativeEmbedEditorPlugin()}
          $hasSidePanelGutter={hasSidePanelGutter}
          $isRightGutterCollapsed={isRightGutterCollapsed}
          $isInAIFCStagingAreaView={isInAIFCStagingAreaView}
          $isObjectSidebarShown={isObjectSidebarShown}
          $isTimelineLayoutEnabled={isTimelineLayoutEnabled}
          $layoutType={layoutType}
          style={
            shouldOffsetTableForPillTabs
              ? { marginTop: token('space.negative.100', '-8px') }
              : undefined
          }
          data-pill-view-tabs-table-offset={
            shouldOffsetTableForPillTabs ? 'true' : undefined
          }
          data-testid="layout-view--wrapper"
        >
          {options.toolbarLocation === 'views' ? (
            <ToolbarRenderer
              isEmbedView={isEmbedView}
              isNativeEmbedView={isNativeEmbedView}
              isRightGutterCollapsed={isRightGutterCollapsed}
              isSidePanelVisible={isSidePanelVisible}
              hasSidePanelGutter={false}
              isObjectSidebarShown={isObjectSidebarShown}
              toolbar={toolbar}
            />
          ) : null}
          {view}
        </ViewWrapper>
        {footer && (
          <Box xcss={footerContainerStyles}>
            <Box xcss={footerContentStyles}>{footer}</Box>
          </Box>
        )}
      </Stack>
    </Panel>
  );
};

export default MainPanel;
